import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
    LsqApiServiceListSource,
    SortDirectionFinancing
} from '@lsq-frontend/angular-shared/api-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import {
    FastTrackRouteQueryParams,
    FeatureFlags
} from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    ACCOUNT_SUPPLIER_DETAILS,
    atlas,
    BUYER_SUPPLIER_DETAILS,
    SUPPLY_CHAIN_DIRECT_ACCOUNT_OVERVIEW,
    SUPPLY_CHAIN_DIRECT_ACCOUNT_PARENT_GROUPED_INVOICE_DETAIL,
    SUPPLY_CHAIN_DIRECT_BUYER_OVERVIEW,
    SUPPLY_CHAIN_DIRECT_BUYER_PARENT_GROUPED_INVOICE_DETAIL
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { CurrencyCode, Money } from '@lsq-frontend/remote-services/all-java/common-mk0';
import {
    GetBuyerInvoiceExportService,
    InvoiceFundingStatus,
    InvoiceSummary,
    RevokeInvoiceApprovalRequest,
    RevokeInvoiceApprovalService
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { GetBuyerAccountPendingGroupRequest } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/invoice-group/get-buyer-account-pending-group';
import {
    ListBuyerAccountPendingGroupsRequest,
    ListResponseBuyerAccountInvoiceGroupSummaryItem
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/invoice-group/list-buyer-account-pending-groups';
import {
    RevokeChildInvoiceRequest,
    RevokeChildInvoiceService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/revoke-child-invoice/revoke-child-invoice';
import { BuyerAccountInvoiceGroupItem } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/buyer-account-invoice-group-item';
import { BuyerAccountInvoiceGroupSummaryItem } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/buyer-account-invoice-group-summary-item';
import { PendingGroupDetail } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/pending-group-detail';
import { DashboardClaim, Guid } from '@lsq-frontend/remote-services/enterprise/common';
import {
    BuyerAccountInvoiceGroupDetailGQL,
    BuyerAccountInvoiceGroupItemGQL,
    GetBuyerAccountPendingGroupService,
    ListBuyerAccountPendingGroupsService
} from '@lsq-frontend/remote-services/financing-graphql';
import {
    Color,
    DateFormat,
    forLifeOf,
    getDynamicRouterLink,
    LifecycleAware,
    Modal,
    StatusColor,
    Table,
    TableAppearance
} from '@lsq-frontend/common-ui';
import {
    ClaimService,
    FeatureFlagService,
    getRouterLinkById,
    Icons,
    ResponsiveService,
    WindowReferenceService
} from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { BehaviorSubject } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { InvoiceFundingStatusDisplay } from '../../../supply-chain-direct.models';
import { InvoiceFundingStatusModifications } from '../../invoice-detail-page/invoice-detail-page.component';
import { SupplyChainDirectAccountService } from '../../supply-chain-direct-account.service';
import { SupplyChainDirectBuyerAccountSharedService } from '../../supply-chain-direct-buyer-account-shared.service';
import { RevokeInvoiceModalComponent } from '../revoke-invoice-modal/revoke-invoice-modal.component';

interface LoadableItem extends BuyerAccountInvoiceGroupItem {
    isRowLoading: boolean;
    groupKey: string;
}

interface ModifiedPendingGroupDetail extends PendingGroupDetail {
    isGroup: boolean;
}

@Component({
    selector: 'supply-chain-direct-account-pending-credit-memos',
    templateUrl: './pending-credit-memos.component.html'
})
@LifecycleAware()
export class AccountPendingCreditMemosComponent implements OnInit {
    public listPendingGroupsDataSource: LsqApiServiceListSource<
        ListResponseBuyerAccountInvoiceGroupSummaryItem,
        ListBuyerAccountPendingGroupsRequest,
        PendingGroupDetail
    >;
    public pendingGroupTogglesAreDisabled = false;
    public readonly tableAppearance = TableAppearance;
    public readonly color = Color;
    public readonly statusColor = StatusColor;
    public readonly icons = Icons;
    public readonly featureFlags = FeatureFlags;
    public readonly lsqShortDateFormat = DateFormat.LsqShort;
    public readonly ineligibleStatus = InvoiceFundingStatus.INELIGIBLE;
    public readonly creditMemoStatus = InvoiceFundingStatusModifications.CREDIT_MEMO;
    public readonly supplierNameI18n = this._i18n({
        value: 'Supplier',
        meaning: `Seller's name`,
        description: 'Table column header'
    });
    public readonly invoiceNumberI18n = this._i18n({
        value: 'Invoice Number',
        meaning: `Invoice number`,
        description: 'Table column header'
    });
    public readonly invoiceDateI18n = this._i18n({
        value: 'Invoice Date',
        meaning: `Invoice date`,
        description: 'Table column header'
    });
    public readonly dueDateI18n = this._i18n({
        value: 'Due Date',
        meaning: `Date the invoice is due to be paid by the buyer`,
        description: 'Table column header'
    });
    public readonly invoiceAmountI18n = this._i18n({
        value: 'Amount',
        meaning: `The money value of the invoice`,
        description: 'Table column header'
    });
    public readonly invoiceStatusI18n = this._i18n({
        value: 'Status',
        meaning: `The eligibility status of the invoice`,
        description: 'Table column header'
    });
    public readonly revokeHeaderI18n = this._i18n({ value: 'Revoke' });
    public readonly revokeModalTitleI18n = this._i18n({ value: 'Revoke Invoice' });
    public readonly revokeGroupModalTitleI18n = this._i18n({ value: 'Revoke Invoices' });
    public readonly businessUnitI18n = this._i18n('Business Unit');

    public readonly getCreditMemosRowClassNameBound = this._getCreditMemosRowClassName.bind(this);
    public readonly goToPendingCredMemoDetailBound = this.goToPendingCredMemoDetail.bind(this);
    public readonly isInvoiceClickableBound = this.isInvoiceClickable.bind(this);
    public readonly revokeButtonIsHiddenBound = this._revokeButtonIsHidden.bind(this);

    public readonly expandedGroupPendingKeys = new Set<string>();
    public readonly nullValue = Table.NullAmount;

    public isFinancingAccountPortalUser = false;

    public canRevokeFeatureFlag$ = this._featureFlagService.isOn(
        this.featureFlags.RevokeCreditMemo
    );

    public readonly invoiceNumberTruncateAfter$ = this.windowReferenceService.resizeStream.pipe(
        map((dimensions) => {
            if (dimensions.width > 1525) {
                return 29;
            }

            if (dimensions.width > 1375) {
                return 18;
            }

            if (dimensions.width > 1199) {
                return 14;
            }

            return 12;
        })
    );

    constructor(
        public invoiceFundingStatusDisplay: InvoiceFundingStatusDisplay,
        public responsiveService: ResponsiveService,
        public getBuyerInvoiceExportService: GetBuyerInvoiceExportService,
        private _claimService: ClaimService,
        private _i18n: I18n,
        private _listBuyerAccountPendingGroupsService: ListBuyerAccountPendingGroupsService,
        private _getBuyerAccountPendingGroupService: GetBuyerAccountPendingGroupService,
        private _featureFlagService: FeatureFlagService,
        private _router: Router,
        private _modalService: ModalService,
        private _revokeInvoiceApprovalService: RevokeInvoiceApprovalService,
        private _revokeChildInvoiceService: RevokeChildInvoiceService,
        private _userService: UserService,
        private _supplyChainDirectAccountService: SupplyChainDirectAccountService,
        public buyerAccountSharedService: SupplyChainDirectBuyerAccountSharedService,
        public windowReferenceService: WindowReferenceService
    ) {
        this.isFinancingAccountPortalUser = this._claimService.isOnSync(
            DashboardClaim.FinancingAccountPortal
        );
    }

    ngOnInit(): void {
        this.listPendingGroupsDataSource = new LsqApiServiceListSource(
            this._listBuyerAccountPendingGroupsService,
            {
                buyerKeys:
                    this._supplyChainDirectAccountService.buyerKeysFormGroup.get('buyerKeys')
                        ?.value ?? [],
                pagination: {
                    sortColumn: 'groupId',
                    sortDirection: SortDirectionFinancing.DESCENDING
                }
            } as ListBuyerAccountPendingGroupsRequest,
            (row) => ({
                ...row,
                isGroup: true,
                groupId: row.groupId,
                canRevoke:
                    row.canRevoke &&
                    this._featureFlagService.isOnSync(this.featureFlags.RevokeCreditMemo)
            })
        );

        // Any time we ask for new invoices, collapse all open groups.
        this.listPendingGroupsDataSource
            .loading$!.pipe(
                forLifeOf(this),
                filter((loading) => loading === true),
                switchMap(() => this._collapseAll())
            )
            .subscribe();

        this._supplyChainDirectAccountService.buyerKeysFormGroup
            .get('buyerKeys')
            ?.valueChanges.pipe(forLifeOf(this))
            .subscribe((buyerKeys) => {
                this.listPendingGroupsDataSource.dispatchRequest({
                    ...this.listPendingGroupsDataSource.request,
                    buyerKeys: buyerKeys || []
                });
            });
    }

    private _revokeButtonIsHidden(
        row: BuyerAccountInvoiceGroupSummaryItem | BuyerAccountInvoiceGroupItemGQL
    ): boolean {
        return !(row.canRevoke as boolean);
    }

    public async handleRevokeClick(
        row: BuyerAccountInvoiceGroupSummaryItem | BuyerAccountInvoiceGroupItemGQL
    ): Promise<void> {
        const isChildInvoice = this._isChildInvoice(row);
        let theInvoiceSummary;
        let theChildren: InvoiceSummary[];
        if (!isChildInvoice) {
            const pendingGroup = await this._getPendingGroup(row.groupKey!);
            theInvoiceSummary = this._getInvoiceSummaryFromPendingGroupRow(
                pendingGroup,
                isChildInvoice
            );
            theChildren = this._getInvoiceSummariesFromBuyerPendingGroupRow(pendingGroup);
        } else {
            theInvoiceSummary = this._getInvoiceSummaryFromPendingGroupRow(row, isChildInvoice);
            theChildren = [];
        }

        const isSubmittingSubject = new BehaviorSubject<boolean>(false);

        this._modalService.showModal(
            new Modal({
                id: 'revoke-invoice',
                title: !isChildInvoice ? this.revokeGroupModalTitleI18n : this.revokeModalTitleI18n,
                component: RevokeInvoiceModalComponent,
                width: '60rem',
                showCloseButton: true,
                data: {
                    buyerInvoice: theInvoiceSummary,
                    childInvoices: theChildren,
                    isPendingPage: true
                },
                isSubmitting$: isSubmittingSubject.asObservable(),
                onOkClick: async () => {
                    isSubmittingSubject.next(true);
                    if (isChildInvoice) {
                        await this._revokeChildInvoiceService.executeOnce(
                            new RevokeChildInvoiceRequest({
                                childInvoiceKey: (row as BuyerAccountInvoiceGroupItemGQL).itemKey
                            })
                        );
                    } else {
                        await this._revokeInvoiceApprovalService.executeOnce(
                            new RevokeInvoiceApprovalRequest({
                                accountKey: this._getAlternateCompanyKey(),
                                invoiceKeys: [row.groupKey!]
                            })
                        );
                    }
                    isSubmittingSubject.next(false);
                    this._modalService.closeModal();
                    this.listPendingGroupsDataSource.dispatchRequest();
                }
            })
        );
    }

    public async handlePendingGroupToggleClick(row: PendingGroupDetail): Promise<void> {
        if (this.pendingGroupTogglesAreDisabled) {
            return;
        }
        this.pendingGroupTogglesAreDisabled = true;
        if (this.expandedGroupPendingKeys.has(row.groupKey!)) {
            await this._collapsePendingGroup(row.groupKey!);
        } else {
            await this._expandPendingGroup(row.groupKey!);
        }
        this.pendingGroupTogglesAreDisabled = false;
    }

    private async _expandPendingGroup(groupKey: string): Promise<void> {
        // Collapse the group if it was somehow already open.
        if (this.expandedGroupPendingKeys.has(groupKey)) {
            await this._collapsePendingGroup(groupKey);
        }

        // Make sure we're expanding a valid `groupKey`.
        let pendingGroupSummaryRows = await this.listPendingGroupsDataSource.rows$
            .pipe(first())
            .toPromise();
        let index = pendingGroupSummaryRows.findIndex((row) => row.groupKey === groupKey);
        if (index < 0) {
            return;
        }

        // Expand the row visually, and add the "loading" row.
        this.expandedGroupPendingKeys.add(groupKey);
        const loadingRow: LoadableItem = {
            isRowLoading: true,
            groupKey,
            itemKey: 'loading'
        };
        this.listPendingGroupsDataSource.expandGroup(index, [loadingRow]);

        // Get the grouped invoices.
        const pendingGroup = await this._getPendingGroup(groupKey);

        // Collapse the loading row.
        await this.listPendingGroupsDataSource.collapseGroup('groupKey', groupKey);

        // Expand the group for real.
        pendingGroupSummaryRows = await this.listPendingGroupsDataSource.rows$
            .pipe(first())
            .toPromise();
        index = pendingGroupSummaryRows.findIndex((row) => row.groupKey === groupKey);
        if (index < 0) {
            return;
        }
        const sellerName = pendingGroupSummaryRows.find((row) => row.groupKey === groupKey)
            ?.sellerName;

        // Make sure to map the Item entity into the PendingGroupSummary interface so that the column attributes line up
        this.listPendingGroupsDataSource.expandGroup(
            index,
            pendingGroup.items!.map((item: BuyerAccountInvoiceGroupItemGQL) => ({
                ...item,
                sellerName,
                groupId: item.invoiceNumber,
                total: item.amount,
                groupKey
            }))
        );
    }

    private async _getPendingGroup(groupKey: string): Promise<BuyerAccountInvoiceGroupDetailGQL> {
        return (
            await this._getBuyerAccountPendingGroupService.executeOnce(
                new GetBuyerAccountPendingGroupRequest({
                    buyerKeys:
                        this._supplyChainDirectAccountService.buyerKeysFormGroup.get('buyerKeys')
                            ?.value ?? [],
                    groupKey
                })
            )
        ).pendingGroup!;
    }

    private async _collapsePendingGroup(groupKey: string): Promise<void> {
        await this.listPendingGroupsDataSource.collapseGroup('groupKey', groupKey);
        this.expandedGroupPendingKeys.delete(groupKey);
    }

    private async _collapseAll(): Promise<void> {
        for (const groupKey of Array.from(this.expandedGroupPendingKeys)) {
            await this._collapsePendingGroup(groupKey);
        }
    }

    public trackByPendingKeys(_index: number, { groupKey }: PendingGroupDetail): Guid {
        return groupKey!;
    }

    public goToPendingCredMemoDetail(creditMemo: ModifiedPendingGroupDetail): void {
        void this._router.navigateByUrl(
            this._router.createUrlTree(
                getDynamicRouterLink(
                    getRouterLinkById(
                        atlas,
                        this.isFinancingAccountPortalUser
                            ? SUPPLY_CHAIN_DIRECT_ACCOUNT_PARENT_GROUPED_INVOICE_DETAIL
                            : SUPPLY_CHAIN_DIRECT_BUYER_PARENT_GROUPED_INVOICE_DETAIL,
                        {
                            noStandaloneLeadingSlash: true
                        }
                    ),
                    { invoiceKey: creditMemo.groupKey }
                ),
                {
                    queryParams: {
                        [FastTrackRouteQueryParams.PreviousRouteId]: PENDING_BUYER_CREDIT_MEMOS_TAB
                    }
                }
            )
        );
    }

    private _getCreditMemosRowClassName(
        row: ModifiedPendingGroupDetail | BuyerAccountInvoiceGroupItemGQL
    ): string {
        const classList = [];
        if ('isGroup' in row && row.isGroup) {
            classList.push('is-group');

            if (this.expandedGroupPendingKeys.has(row.groupKey!)) {
                classList.push('expanded');
            }
        }

        if ('itemKey' in row) {
            classList.push('is-member-of-group');
        }

        return classList.join(' ');
    }

    public isInvoiceClickable(row: ModifiedPendingGroupDetail): boolean {
        return (
            row.isGroup && this._featureFlagService.isOnSync(FeatureFlags.PendingCreditMemosDetails)
        );
    }

    private _isChildInvoice(row: Object): row is BuyerAccountInvoiceGroupItemGQL {
        return (row as BuyerAccountInvoiceGroupItemGQL).dueIn !== undefined;
    }

    private _getAlternateCompanyKey(): string {
        return this._userService.getAlternateCompanyKey();
    }

    private _getChildInvoiceSummaryFromPendingGroupRow(
        pendingGroupDetail: BuyerAccountInvoiceGroupItemGQL
    ): InvoiceSummary {
        const invoiceSummary = new InvoiceSummary();
        invoiceSummary.isGroup = false;
        invoiceSummary.invoiceNumber = pendingGroupDetail.invoiceNumber!;

        return invoiceSummary;
    }

    private _getInvoiceSummariesFromBuyerPendingGroupRow(
        pendingGroupDetail: BuyerAccountInvoiceGroupDetailGQL
    ): InvoiceSummary[] {
        const childArray = [];

        for (let i = 0; i < pendingGroupDetail.items!.length; i++) {
            const invSummary = //new InvoiceSummary();
                //invSummary =
                this._getChildInvoiceSummaryFromPendingGroupRow(pendingGroupDetail.items?.[i]!);
            childArray[i] = invSummary;
        }

        return childArray;
    }

    private _getInvoiceSummaryFromPendingGroupRow(
        pendingGroupDetail: PendingGroupDetail | BuyerAccountInvoiceGroupItemGQL,
        isChildInvoice: boolean
    ): InvoiceSummary {
        const invoiceSummary = new InvoiceSummary();
        invoiceSummary.isGroup = !isChildInvoice;
        invoiceSummary.sellerName = (pendingGroupDetail as PendingGroupDetail).sellerName!;
        invoiceSummary.groupId = isChildInvoice
            ? (pendingGroupDetail as BuyerAccountInvoiceGroupItemGQL).itemKey!
            : pendingGroupDetail.groupId!;
        invoiceSummary.invoiceNumber = pendingGroupDetail.invoiceNumber!;
        invoiceSummary.invoiceDate = new Date(pendingGroupDetail.invoiceDate!);
        invoiceSummary.invoiceDueDate = new Date(pendingGroupDetail.dueDate!);

        const invoiceAmount = new Money();
        invoiceAmount.cents = (pendingGroupDetail as PendingGroupDetail).total!.cents!;
        invoiceAmount.currencyCode = CurrencyCode.USD;
        invoiceSummary.invoiceAmount = invoiceAmount;
        invoiceSummary.finalApprovedAmount = invoiceAmount;

        return invoiceSummary;
    }

    public goSupplierDetails(sellerKey: string) {
        this._router.navigateByUrl(
            this._router.createUrlTree(
                getDynamicRouterLink(
                    getRouterLinkById(
                        atlas,
                        this.isFinancingAccountPortalUser
                            ? ACCOUNT_SUPPLIER_DETAILS
                            : BUYER_SUPPLIER_DETAILS,
                        {
                            noStandaloneLeadingSlash: true
                        }
                    ),
                    { sellerKey }
                ),
                {
                    queryParams: {
                        [FastTrackRouteQueryParams.PreviousRouteId]: this
                            .isFinancingAccountPortalUser
                            ? SUPPLY_CHAIN_DIRECT_ACCOUNT_OVERVIEW
                            : SUPPLY_CHAIN_DIRECT_BUYER_OVERVIEW
                    }
                }
            )
        );
    }
}

export const PENDING_BUYER_CREDIT_MEMOS_TAB = 'PENDING_BUYER_CREDIT_MEMOS_TAB';
